// Job status machine: THE single definition (14_US_COMPAT_MASTER_PLAN C3). Imported by the API (setStatus), the worker,
// the ops app (P2), the tech app (P3), dispatch (P5), payments (P6) and parts/passport (P7). Mirrored by the DB:
// ops.jobs.order_status CHECK, ops.jobs.job_step CHECK and ops.status_allowed_sources(); check-contracts fails on drift.
//
// Vocabulary: India's `order_status` strings VERBATIM (India backend server.js:170-193, 471-513, 1871-1925), so ported India UI / Temporal code compares the same strings with zero diff. US users only ever see
// US_LABELS. US additions are marked "US:".

// ── India verbatim ──────────────────────────────────────────────────────────────────────────────────────────────────
/** server.js:170 — order_status → Temporal event_type. */
export const ORDER_STATUS_TO_EVENT_MAP = /** @type {const} */ ({
  'CONFIRMED': 'booking_created',
  'MECHANIC ALLOCATED': 'mechanic_assigned',
  'MECHANIC CONFIRMED': 'mechanic_accepted',
  'MECHANIC DENIED': 'mechanic_rejected',
  'CUSTOMER CALLED': 'customer_dialed',
  'MECHANIC CALLED CUSTOMER': 'customer_dialed',
  'CUSTOMER PHONE RINGING': 'customer_phone_ringing',
  'CUSTOMER ANSWERED': 'customer_answered',
  'CUSTOMER DID NOT ANSWER': 'customer_no_answer',
  'NO RESPONSE': 'customer_no_answer',
  'CUSTOMER DETAILS SENT': 'mechanic_going_yes',
  'MECHANIC DID NOT REACH': 'mechanic_did_not_reach',
  'MECHANIC DISCONNECTED': 'mechanic_disconnected',
  'MECHANIC REACHED': 'mechanic_reached_customer_location',
  'IN PROGRESS - AUTO': 'mechanic_reached_customer_location',
  'SERVICE STARTED': 'service_started',
  'IN PROGRESS': 'service_in_progress',
  'IN PROGRESS - VEHICLE TOWED': 'service_towed',
  'IN PROGRESS - BATTERY TOWED': 'service_towed',
  'INVOICE GENERATED': 'invoice_generated',
  'COMPLETED': 'booking_completed',
  'CASH TAKEN': 'booking_completed',
  'DUPLICATE': 'ops_manual_update',
});

/** server.js:196 — statuses whose Temporal event is sent by a dedicated endpoint. */
export const SKIP_TEMPORAL_IN_STATUS_UPDATE = /** @type {const} */ (['MECHANIC CONFIRMED', 'MECHANIC DENIED']);

/** server.js:486 */
export const CANCELLED_STATUSES = /** @type {const} */ ([
  'CANCELLED - NON SERVICEABLE',
  'CANCELLED - MECHANIC DENIED',
  'CANCELLED - MECHANIC NOT AVAILABLE',
  'CANCELLED - CUSTOMER DID NOT RESPOND',
  'CANCELLED - CUSTOMER DENIED',
  'CANCELLED - EXPENSIVE',
  'CANCELLED - RSA',
  'CANCELLED - RAIN',
  'CANCELLED - ENQUIRY',
  'CANCELLED - SPARES NOT AVAILABLE',
  'CANCELLED - EV VEHICLE',
  'CANCELLED - AFTER HOURS',
  'CANCELLED - OTHER REASONS',
]);

/** server.js:502 */
export const TOWED_STATUSES = /** @type {const} */ (['IN PROGRESS - VEHICLE TOWED', 'IN PROGRESS - BATTERY TOWED']);

/** server.js:507 */
export const TERMINAL_STATUSES = /** @type {const} */ ([...CANCELLED_STATUSES, 'DUPLICATE', 'COMPLETED', 'CASH TAKEN', 'SETTLED']);

/** server.js:1877-1933 (GET /api/booking_statuses), plus the US status inserted in display order. */
export const IN_PROGRESS_STATUSES = /** @type {const} */ (['IN PROGRESS', 'IN PROGRESS - AUTO', ...TOWED_STATUSES]);
export const COMPLETED_STATUSES = /** @type {const} */ (['COMPLETED', 'CASH TAKEN', 'SETTLED']);

/** US: estimate sent, waiting for the customer's approval (P7 written authorization). Not an India value. */
export const AWAITING_APPROVAL = 'AWAITING APPROVAL';

export const DISPLAY_ORDER = /** @type {const} */ ([
  'REWORK', 'CONFIRMED', 'ASSIGNED', 'CUSTOMER CONFIRMED', 'LOCATION CONFIRMED', 'MECHANIC ALLOCATED', 'MECHANIC DENIED',
  'RETRYING IVR ALLOCATION', 'MECHANIC CONFIRMED', 'CUSTOMER ANSWERED', 'REMINDER INITIATED', 'CUSTOMER DETAILS SENT',
  'CUSTOMER CALLED', 'MECHANIC DID NOT REACH', 'MECHANIC DISCONNECTED', 'MECHANIC REACHED', 'IN PROGRESS',
  AWAITING_APPROVAL, // US
  'SERVICE STARTED', 'IN PROGRESS - AUTO', ...TOWED_STATUSES, 'INVOICE GENERATED', 'CASH TAKEN', 'NO PAYMENT RECEIVED',
  'COMPLETED', 'NO RESPONSE', ...CANCELLED_STATUSES, 'DUPLICATE',
]);

/** India dashboard colors (server.js:1911), kept so ported board components render unchanged. */
export const STATUS_COLORS = /** @type {const} */ ({
  'SERVICE STARTED': 'orchid', 'IN PROGRESS - AUTO': 'orchid', 'IN PROGRESS': 'chartreuse',
  'IN PROGRESS - VEHICLE TOWED': 'lightskyblue', 'IN PROGRESS - BATTERY TOWED': '#5fa8d3', 'COMPLETED': 'lightgray',
  'CASH TAKEN': 'teal', 'MECHANIC ALLOCATED': 'lightpink', 'CUSTOMER DETAILS SENT': 'lightsteelblue',
  'CUSTOMER CONFIRMED': '#8aeaaa', 'LOCATION CONFIRMED': '#8ddb51', 'MECHANIC CONFIRMED': 'lightgoldenrodyellow',
  'CUSTOMER CALLED': 'lightsalmon', 'CONFIRMED': 'lightyellow', 'ASSIGNED': 'lightgreen',
  'RETRYING IVR ALLOCATION': 'lightgoldenrodyellow', 'NO RESPONSE': 'gold', 'CUSTOMER ANSWERED': '#e6e6fa',
  'MECHANIC REACHED': 'lightcyan', 'MECHANIC DID NOT REACH': 'lightpurple', 'MECHANIC DENIED': 'lightpurple',
  'MECHANIC DISCONNECTED': 'lightgray', 'REMINDER INITIATED': 'lightseagreen', 'REWORK': '#f593de',
  'NO PAYMENT RECEIVED': '#ff6347', 'DUPLICATE': 'lightcoral',
  [AWAITING_APPROVAL]: '#ffd59e', // US
  'INVOICE GENERATED': '#c7e9c0', // US (India has no color for it)
});

/** Every legal ops.jobs.order_status value (DB CHECK mirrors this exactly). */
export const ORDER_STATUSES = /** @type {const} */ ([...new Set([
  ...DISPLAY_ORDER, ...Object.keys(ORDER_STATUS_TO_EVENT_MAP), ...TERMINAL_STATUSES,
])]);

/** server.js:477 verbatim. */
export function isTerminalStatus(orderStatus) {
  if (!orderStatus) return false;
  return orderStatus === 'COMPLETED'
    || orderStatus === 'CASH TAKEN'
    || orderStatus === 'NO PAYMENT RECEIVED'
    || orderStatus === 'SETTLED'
    || orderStatus === 'DUPLICATE'
    || orderStatus.startsWith('CANCELLED');
}

/** server.js:471 verbatim (US: AWAITING APPROVAL → estimate_sent). */
export function getEventTypeForStatus(orderStatus) {
  if (orderStatus && orderStatus.startsWith('CANCELLED')) return 'booking_cancelled';
  if (orderStatus === AWAITING_APPROVAL) return 'estimate_sent';
  return ORDER_STATUS_TO_EVENT_MAP[orderStatus] || 'ops_manual_update';
}

// ── US display ──────────────────────────────────────────────────────────────────────────────────────────────────────
/** What US users see. Every ORDER_STATUSES value has a label (check-contracts enforces it). */
export const US_LABELS = /** @type {const} */ ({
  'REWORK': 'Rework',
  'CONFIRMED': 'Booked',
  'ASSIGNED': 'Assigned',
  'CUSTOMER CONFIRMED': 'Customer confirmed',
  'LOCATION CONFIRMED': 'Location confirmed',
  'MECHANIC ALLOCATED': 'Tech assigned',
  'MECHANIC DENIED': 'Tech declined',
  'RETRYING IVR ALLOCATION': 'Finding a tech',
  'MECHANIC CONFIRMED': 'Tech accepted',
  'CUSTOMER ANSWERED': 'Customer answered',
  'REMINDER INITIATED': 'Reminder sent',
  'CUSTOMER DETAILS SENT': 'On the way',
  'CUSTOMER CALLED': 'Calling customer',
  'MECHANIC CALLED CUSTOMER': 'Tech called customer',
  'CUSTOMER PHONE RINGING': 'Customer phone ringing',
  'CUSTOMER DID NOT ANSWER': 'Customer did not answer',
  'MECHANIC DID NOT REACH': 'Tech did not arrive',
  'MECHANIC DISCONNECTED': 'Tech unreachable',
  'MECHANIC REACHED': 'Tech on site',
  'IN PROGRESS': 'In progress',
  [AWAITING_APPROVAL]: 'Awaiting approval',
  'SERVICE STARTED': 'Work started',
  'IN PROGRESS - AUTO': 'On site (auto)',
  'IN PROGRESS - VEHICLE TOWED': 'Vehicle towed',
  'IN PROGRESS - BATTERY TOWED': 'Battery towed',
  'INVOICE GENERATED': 'Invoiced',
  'CASH TAKEN': 'Paid offline',
  'NO PAYMENT RECEIVED': 'Unpaid',
  'COMPLETED': 'Paid',
  'SETTLED': 'Settled',
  'NO RESPONSE': 'No response',
  'CANCELLED - NON SERVICEABLE': 'Cancelled: outside service area',
  'CANCELLED - MECHANIC DENIED': 'Cancelled: tech declined',
  'CANCELLED - MECHANIC NOT AVAILABLE': 'Cancelled: no tech available',
  'CANCELLED - CUSTOMER DID NOT RESPOND': 'Cancelled: customer unreachable',
  'CANCELLED - CUSTOMER DENIED': 'Cancelled by customer',
  'CANCELLED - EXPENSIVE': 'Cancelled: price',
  'CANCELLED - RSA': 'Cancelled: roadside',
  'CANCELLED - RAIN': 'Cancelled: weather',
  'CANCELLED - ENQUIRY': 'Cancelled: enquiry only',
  'CANCELLED - SPARES NOT AVAILABLE': 'Cancelled: parts unavailable',
  'CANCELLED - EV VEHICLE': 'Cancelled: EV',
  'CANCELLED - AFTER HOURS': 'Cancelled: after hours',
  'CANCELLED - OTHER REASONS': 'Cancelled: other',
  'DUPLICATE': 'Duplicate',
});

/** Valid but never offered in US pickers (India-specific reasons). */
export const US_HIDDEN = /** @type {const} */ (['CANCELLED - RAIN', 'CANCELLED - EV VEHICLE', 'CANCELLED - RSA', 'IN PROGRESS - BATTERY TOWED']);

/** India call/allocation "statuses" a tenant may fold into the timeline instead of the board (tenant setting). */
export const CALL_EVENT_STATUSES = /** @type {const} */ ([
  'CUSTOMER CALLED', 'MECHANIC CALLED CUSTOMER', 'CUSTOMER PHONE RINGING', 'CUSTOMER ANSWERED', 'CUSTOMER DID NOT ANSWER',
  'NO RESPONSE', 'MECHANIC DENIED', 'MECHANIC DISCONNECTED', 'RETRYING IVR ALLOCATION', 'REMINDER INITIATED',
]);

// ── Authority (port of update_booking_status guards 5b–11 + skill booking_status_authority) ─────────────────────────
/**
 * Who is setting the status. `stripe_webhook` is the US equivalent of India's payment-link webhook source. `settle_service`
 * is P6's payout settle (the India settle endpoints). Every setStatus call names exactly one. Only these strings are
 * accepted; India source names are not (14 §6.3).
 */
export const STATUS_SOURCES = /** @type {const} */ ([
  'staff',           // office user through the app (permission-checked)
  'tech',            // assigned technician (assignment-checked)
  'customer_link',   // end-customer via an access link (approve / decline)
  'system',          // API-internal automation (worker tasks, onboarding)
  'dispatch',        // P5 dispatch / offers (in-process or /internal)
  'geofence',        // P5 geo.evaluate arrival flip from tech GPS (system actor)
  'temporal',        // P5 Temporal workflows via /internal/v1
  'voice_ai',        // P4 AI receptionist
  'stripe_webhook',  // P6: payment confirmed
  'settle_service',  // P6: payout settlement (India settle_* endpoints)
  'owner_override',  // tenant owner break-glass (reason required, audited)
]);

/**
 * Statuses with a restricted setter. The DB trigger ops.jobs_status_guard enforces the same sets through
 * ops.status_allowed_sources() (check-contracts compares them). Any status not listed: any source (rules below apply).
 */
export const STATUS_AUTHORITY = /** @type {const} */ ({
  'COMPLETED': ['stripe_webhook'],                                // guard 7c (+7d flip): payment webhook only, never override
  'SETTLED': ['settle_service'],                                  // guard 7b: never manual, never override
  'CASH TAKEN': ['staff'],                                        // + permission jobs.record_offline_payment; never override
});
/** Staff permission needed per restricted status (checked by setStatus for source 'staff'). */
export const STATUS_PERMISSION = /** @type {const} */ ({ 'CASH TAKEN': 'jobs.record_offline_payment' });
/** Statuses a technician may set on their own assigned job (India mechanic app quick actions, minus CASH TAKEN). */
export const TECH_SETTABLE = /** @type {const} */ ([
  'MECHANIC CONFIRMED', 'MECHANIC DENIED', 'CUSTOMER DETAILS SENT', 'MECHANIC CALLED CUSTOMER', 'MECHANIC REACHED',
  'IN PROGRESS', 'SERVICE STARTED', AWAITING_APPROVAL, 'INVOICE GENERATED', 'NO RESPONSE', ...TOWED_STATUSES,
]);

/** India `validStatuses` of update_booking_status (server.js:16430) — the "work / closing" set — plus US additions. */
export const WORK_STATUSES = /** @type {const} */ ([
  'IN PROGRESS', ...TOWED_STATUSES, 'IN PROGRESS - AUTO', 'COMPLETED', 'CASH TAKEN', 'SETTLED', ...CANCELLED_STATUSES,
  'DUPLICATE', 'SERVICE STARTED', 'NO PAYMENT RECEIVED',
  AWAITING_APPROVAL, 'INVOICE GENERATED', // US
]);

/**
 * Pure transition check — the ported guards, in India's order. Returns {ok:true, anomaly?} or {ok:false, code, rule}.
 * India returned a silent HTTP 200 on a block; the US API returns 409 `status_not_allowed` (P1 §2).
 * @param {{from:string|null, to:string, source:string, hasTech:boolean, reason?:string}} a
 */
export function checkTransition({ from, to, source, hasTech, reason }) {
  const src = source;
  const no = (rule, code = 'status_not_allowed') => ({ ok: false, code, rule });
  if (!ORDER_STATUSES.includes(to)) return no('unknown_status', 'unknown_status');
  if (!STATUS_SOURCES.includes(src)) return no('unknown_source', 'unknown_source');
  if (src === 'owner_override' && !(reason && String(reason).trim())) return no('override_needs_reason', 'reason_required');
  const existing = from || '';
  if (existing === to) return { ok: true, noop: true };

  // 6b. SETTLED is absolutely final.
  if (existing === 'SETTLED') return no('6b_settled_final');
  // Authority table (7b SETTLED, 7c COMPLETED, US CASH TAKEN).
  const allowed = STATUS_AUTHORITY[to];
  if (allowed && !allowed.includes(src)) return no(`authority_${to}`);
  // US: terminal → non-terminal only by owner_override (India had no path back except override_booking_status).
  if (isTerminalStatus(existing) && !isTerminalStatus(to) && !TOWED_STATUSES.includes(to) && src !== 'owner_override') {
    // India 7 allows COMPLETED/CASH TAKEN → TOWED; everything else from a terminal needs the owner.
    return no('terminal_reopen');
  }
  if (src === 'owner_override') return { ok: true };

  // 5b. NO PAYMENT RECEIVED blocked on cancelled/duplicate.
  if (to === 'NO PAYMENT RECEIVED' && [...CANCELLED_STATUSES, 'DUPLICATE'].includes(existing)) return no('5b');
  // 6. SERVICE STARTED blocked after completion/cancel/tow.
  if (to === 'SERVICE STARTED' && ['COMPLETED', 'CASH TAKEN', 'SETTLED', ...CANCELLED_STATUSES, 'DUPLICATE', ...TOWED_STATUSES].includes(existing)) return no('6');
  // 7. From COMPLETED / CASH TAKEN only to same, SETTLED, NO PAYMENT RECEIVED, TOWED (and the 7d flip).
  if ((existing === 'COMPLETED' || existing === 'CASH TAKEN')
      && !(to === 'COMPLETED' || to === 'CASH TAKEN' || to === 'SETTLED' || to === 'NO PAYMENT RECEIVED' || TOWED_STATUSES.includes(to))) return no('7');
  // 7d. CASH TAKEN → COMPLETED only stripe_webhook. Covered by authority (money statuses are never override targets), kept explicit.
  if (to === 'COMPLETED' && existing === 'CASH TAKEN' && src !== 'stripe_webhook') return no('7d');
  // 7e. A serviced job must have a tech — except a real payment webhook (allowed, flagged as anomaly).
  let anomaly = null;
  if ((to === 'CASH TAKEN' || to === 'COMPLETED') && !hasTech) {
    if (src !== 'stripe_webhook') return no('7e_no_tech', 'no_tech_assigned');
    anomaly = 'completed_without_tech';
  }
  // 8. From IN PROGRESS - AUTO / SERVICE STARTED only the ported whitelist (+US INVOICE GENERATED).
  if (['IN PROGRESS - AUTO', 'SERVICE STARTED'].includes(existing)) {
    const allowedForAuto = ['SERVICE STARTED', 'COMPLETED', 'CASH TAKEN', ...CANCELLED_STATUSES, 'DUPLICATE', ...TOWED_STATUSES,
      'NO PAYMENT RECEIVED', 'INVOICE GENERATED'];
    if (!allowedForAuto.includes(to)) return no('8');
  }
  // 9. From TOWED only closing statuses.
  if (TOWED_STATUSES.includes(existing)) {
    const allowedForTowed = ['COMPLETED', 'CASH TAKEN', ...CANCELLED_STATUSES, 'DUPLICATE', ...TOWED_STATUSES, 'NO PAYMENT RECEIVED', 'INVOICE GENERATED'];
    if (!allowedForTowed.includes(to)) return no('9');
  }
  // US: from INVOICE GENERATED only closing statuses (the invoice is out; work is done).
  if (existing === 'INVOICE GENERATED') {
    const allowedForInvoiced = ['COMPLETED', 'CASH TAKEN', 'NO PAYMENT RECEIVED', ...CANCELLED_STATUSES, 'DUPLICATE'];
    if (!allowedForInvoiced.includes(to)) return no('us_invoiced');
  }
  // 10. IN PROGRESS - AUTO blocked on cancelled.
  if (to === 'IN PROGRESS - AUTO' && CANCELLED_STATUSES.includes(existing)) return no('10');
  // 11. Once in the work set, pre-work statuses (allocation / call events) can no longer be set.
  if (!WORK_STATUSES.includes(to) && WORK_STATUSES.includes(existing)) return no('11');
  return anomaly ? { ok: true, anomaly } : { ok: true };
}

// ── Technician job steps (P3; port of routes/mechanic_wizard_v2.js) ─────────────────────────────────────────────────
/** mechanic_wizard_v2.js:23 verbatim. Strict +1 ordering; stored in ops.jobs.job_step (NULL = not started). */
export const JOB_STEPS = /** @type {const} */ (['ACCEPTED', 'EN_ROUTE', 'REACHED', 'INSPECT', 'ESTIMATE', 'APPROVE',
  'SERVICE', 'PHOTOS', 'INVOICE', 'COLLECT', 'COMPLETE']);
/** mechanic_wizard_v2.js:27 — COMPLETE is webhook-only; the app (and even office overrides) may never write past this. */
export const APP_WRITABLE_MAX = 'COLLECT';
/**
 * order_status a step moves the job to (null = no status change). Applied through setStatus with source 'tech'; a
 * step whose status is refused by checkTransition still records the step (the status just stays).
 */
export const JOB_STEP_STATUS = /** @type {const} */ ({
  ACCEPTED: 'MECHANIC CONFIRMED',
  EN_ROUTE: 'CUSTOMER DETAILS SENT',
  REACHED: 'MECHANIC REACHED',
  INSPECT: 'IN PROGRESS',
  ESTIMATE: null,          // sending the estimate (estimates.send) sets AWAITING APPROVAL
  APPROVE: null,           // customer approval (link / staff) sets IN PROGRESS
  SERVICE: 'SERVICE STARTED',
  PHOTOS: null,
  INVOICE: 'INVOICE GENERATED',
  COLLECT: null,
  COMPLETE: null,          // COMPLETED comes only from stripe_webhook
});
/** Preconditions (mechanic_wizard_v2.js:157-176 + P3 §3.3). Enforced by the tech module. */
export const JOB_STEP_PRECONDITIONS = /** @type {const} */ ({
  APPROVE: 'estimate_exists_if_required',
  SERVICE: 'approved_if_estimate_required',
  PHOTOS: 'min_after_photos',
  INVOICE: 'has_invoice_line',
  COLLECT: 'pay_link_sent_or_offline_payment',
});

/** @param {string|null} current @param {string} next */
export function checkStepAdvance(current, next) {
  if (!JOB_STEPS.includes(next)) return { ok: false, code: 'unknown_step' };
  if (JOB_STEPS.indexOf(next) > JOB_STEPS.indexOf(APP_WRITABLE_MAX)) return { ok: false, code: 'complete_is_webhook_only' };
  const have = current ? JOB_STEPS.indexOf(current) : -1;
  if (JOB_STEPS.indexOf(next) !== have + 1) return { ok: false, code: 'step_out_of_order', expected: JOB_STEPS[have + 1] || null };
  return { ok: true };
}

/** Payment status of a job (independent of order_status; P6 owns the writes). */
export const PAYMENT_STATUSES = /** @type {const} */ (['unpaid', 'partial', 'paid', 'refunded']);
